// src/services/contentService.ts
// Content Management API Service
// WFT Study Buddy - Content Management System

import axios from 'axios';
import { auth } from '../config/firebase';

// Types
export type ArticleStatus = 'draft' | 'review' | 'published' | 'archived';
export type ArticleCategory = 'examens' | 'begrippen' | 'studietips' | 'statistieken';

export interface FAQItem {
  question: string;
  answer: string;
}

export interface CitableFact {
  fact: string;
  source: string;
}

export interface ArticleValidation {
  overallScore: number;
  factCheckScore: number;
  seoScore: number;
  readabilityScore: number;
  geoScore: number;
  issues: string[];
  suggestions: string[];
  validatedAt: Date | null;
  validatedBy: 'ai' | 'manual' | null;
}

export interface ArticleStats {
  views: number;
  uniqueViews: number;
  avgTimeOnPage: number;
  scrollDepth: number;
  ctaClicks: number;
  conversions: number;
}

export interface Article {
  id: string;
  slug: string;
  category: ArticleCategory;
  title: string;
  metaDescription: string;
  keywords: string[];
  canonicalUrl: string;
  h1: string;
  intro: string;
  body: string;
  wordCount: number;
  readTimeMinutes: number;
  faqSchema: FAQItem[];
  citableFacts: CitableFact[];
  speakableSections: string[];
  relatedArticles: string[];
  linkedTerms: string[];
  relatedModules: string[];
  validation: ArticleValidation;
  status: ArticleStatus;
  source: 'ai_generated' | 'manual';
  createdAt: Date;
  updatedAt: Date;
  publishedAt: Date | null;
  createdBy: string;
  stats: ArticleStats;
}

export interface ArticleListResponse {
  articles: Article[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface ContentAdminStats {
  total: number;
  byStatus: {
    draft: number;
    review: number;
    published: number;
    archived: number;
  };
  byCategory: Record<ArticleCategory, number>;
  recentlyCreated: number;
  pendingReview: number;
  lowScoreArticles: number;
  avgValidationScore: number;
}

export interface ReviewQueueItem {
  article: Article;
  priority: 'high' | 'medium' | 'low';
  reason: string;
  daysInQueue: number;
}

export interface GenerateArticleRequest {
  topic: string;
  category: ArticleCategory;
  targetKeywords: string[];
  relatedModules?: string[];
  tone?: 'formal' | 'friendly' | 'educational';
  wordCount?: number;
}

export interface CategoryInfo {
  id: string;
  name: string;
  description: string;
  icon: string;
  slug: string;
  order: number;
  articleCount: number;
}

// Constants
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001';

export const CATEGORY_OPTIONS: { value: ArticleCategory; label: string; icon: string }[] = [
  { value: 'examens', label: 'Examens', icon: '📝' },
  { value: 'begrippen', label: 'Begrippen', icon: '📚' },
  { value: 'studietips', label: 'Studietips', icon: '💡' },
  { value: 'statistieken', label: 'Statistieken', icon: '📊' },
];

export const STATUS_OPTIONS: { value: ArticleStatus; label: string; color: string }[] = [
  { value: 'draft', label: 'Draft', color: 'gray' },
  { value: 'review', label: 'Review', color: 'yellow' },
  { value: 'published', label: 'Published', color: 'green' },
  { value: 'archived', label: 'Archived', color: 'red' },
];

export const WFT_MODULES = [
  { value: 'basis', label: 'WFT Basis' },
  { value: 'schade-particulier', label: 'Schade Particulier' },
  { value: 'schade-zakelijk', label: 'Schade Zakelijk' },
  { value: 'hypotheken', label: 'Hypotheken' },
  { value: 'pensioen', label: 'Pensioen' },
  { value: 'vermogen', label: 'Vermogen' },
  { value: 'inkomen', label: 'Inkomen' },
  { value: 'consumptief-krediet', label: 'Consumptief Krediet' },
  { value: 'zorgverzekeringen', label: 'Zorgverzekeringen' },
];

// ============================================
// HELPER FUNCTIONS
// ============================================

const getAuthToken = async (): Promise<string> => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Niet ingelogd');
  }
  return user.getIdToken();
};

// ============================================
// PUBLIC API (geen auth nodig)
// ============================================

/**
 * Haal categorieën op
 */
export const getCategories = async (): Promise<CategoryInfo[]> => {
  try {
    const response = await axios.get(`${BACKEND_URL}/api/content/categories`);
    return response.data;
  } catch (error: any) {
    console.error('Fout bij ophalen categorieën', error);
    throw new Error(error.response?.data?.message || error.message || 'Fout bij ophalen categorieën');
  }
};

/**
 * Haal gepubliceerde artikelen op (public)
 */
export const getPublishedArticles = async (params?: {
  category?: ArticleCategory;
  page?: number;
  limit?: number;
}): Promise<ArticleListResponse> => {
  try {
    const response = await axios.get(`${BACKEND_URL}/api/content/articles`, { params });
    return response.data;
  } catch (error: any) {
    console.error('Fout bij ophalen artikelen', error);
    throw new Error(error.response?.data?.message || error.message || 'Fout bij ophalen artikelen');
  }
};

/**
 * Haal een artikel op via slug (public)
 */
export const getArticleBySlug = async (slug: string): Promise<Article | null> => {
  try {
    const response = await axios.get(`${BACKEND_URL}/api/content/articles/${slug}`);
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      return null;
    }
    console.error('Fout bij ophalen artikel', error);
    throw new Error(error.response?.data?.message || error.message || 'Fout bij ophalen artikel');
  }
};

// ============================================
// ADMIN API (auth nodig)
// ============================================

/**
 * Haal alle artikelen op (admin - alle statuses)
 */
export const getAdminArticles = async (params?: {
  category?: ArticleCategory;
  status?: ArticleStatus;
  search?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}): Promise<ArticleListResponse> => {
  try {
    const token = await getAuthToken();
    const response = await axios.get(`${BACKEND_URL}/api/content/admin/articles`, {
      params,
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  } catch (error: any) {
    console.error('Fout bij ophalen admin artikelen', error);
    throw new Error(error.response?.data?.message || error.message || 'Fout bij ophalen admin artikelen');
  }
};

/**
 * Haal een artikel op via ID (admin)
 */
export const getArticleById = async (id: string): Promise<Article | null> => {
  try {
    const token = await getAuthToken();
    const response = await axios.get(`${BACKEND_URL}/api/content/admin/articles/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      return null;
    }
    console.error('Fout bij ophalen artikel', error);
    throw new Error(error.response?.data?.message || error.message || 'Fout bij ophalen artikel');
  }
};

/**
 * Maak een nieuw artikel (admin)
 */
export const createArticle = async (data: Partial<Article>): Promise<Article> => {
  try {
    const token = await getAuthToken();
    const response = await axios.post(`${BACKEND_URL}/api/content/admin/articles`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  } catch (error: any) {
    console.error('Fout bij aanmaken artikel', error);
    throw new Error(error.response?.data?.message || error.message || 'Fout bij aanmaken artikel');
  }
};

/**
 * Update een artikel (admin)
 */
export const updateArticle = async (id: string, data: Partial<Article>): Promise<Article> => {
  try {
    const token = await getAuthToken();
    const response = await axios.put(`${BACKEND_URL}/api/content/admin/articles/${id}`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  } catch (error: any) {
    console.error('Fout bij updaten artikel', error);
    throw new Error(error.response?.data?.message || error.message || 'Fout bij updaten artikel');
  }
};

/**
 * Verwijder een artikel (admin)
 */
export const deleteArticle = async (id: string): Promise<void> => {
  try {
    const token = await getAuthToken();
    await axios.delete(`${BACKEND_URL}/api/content/admin/articles/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  } catch (error: any) {
    console.error('Fout bij verwijderen artikel', error);
    throw new Error(error.response?.data?.message || error.message || 'Fout bij verwijderen artikel');
  }
};

/**
 * Wijzig artikel status (admin)
 */
export const updateArticleStatus = async (id: string, status: ArticleStatus): Promise<Article> => {
  try {
    const token = await getAuthToken();
    const response = await axios.patch(
      `${BACKEND_URL}/api/content/admin/articles/${id}/status`,
      { status },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  } catch (error: any) {
    console.error('Fout bij wijzigen status', error);
    throw new Error(error.response?.data?.message || error.message || 'Fout bij wijzigen status');
  }
};

/**
 * Publiceer een artikel (admin)
 */
export const publishArticle = async (id: string): Promise<Article> => {
  try {
    const token = await getAuthToken();
    const response = await axios.patch(
      `${BACKEND_URL}/api/content/admin/articles/${id}/publish`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  } catch (error: any) {
    console.error('Fout bij publiceren artikel', error);
    throw new Error(error.response?.data?.message || error.message || 'Fout bij publiceren artikel');
  }
};

/**
 * Unpublish een artikel (admin)
 */
export const unpublishArticle = async (id: string): Promise<Article> => {
  try {
    const token = await getAuthToken();
    const response = await axios.patch(
      `${BACKEND_URL}/api/content/admin/articles/${id}/unpublish`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  } catch (error: any) {
    console.error('Fout bij unpublishen artikel', error);
    throw new Error(error.response?.data?.message || error.message || 'Fout bij unpublishen artikel');
  }
};

/**
 * Regenereer GEO velden (FAQ, citeerbare feiten, speakable sections) met AI
 */
export const regenerateGEOField = async (
  articleId: string,
  field?: 'faq' | 'citableFacts' | 'speakableSections'
): Promise<{
  message: string;
  generated: {
    faqSchema?: FAQItem[];
    citableFacts?: CitableFact[];
    speakableSections?: string[];
  };
  article: Article;
}> => {
  try {
    const token = await getAuthToken();
    const response = await axios.post(
      `${BACKEND_URL}/api/content/regenerate/${articleId}`,
      { field },
      {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 60000
      }
    );
    return response.data;
  } catch (error: any) {
    console.error('Fout bij regenereren GEO veld', error);
    throw new Error(error.response?.data?.message || error.message || 'Fout bij regenereren GEO veld');
  }
};

// ============================================
// AI GENERATION API
// ============================================

/**
 * Genereer een artikel met AI (admin)
 */
export const generateArticle = async (request: GenerateArticleRequest): Promise<{
  article: Article;
  validation: ArticleValidation;
  generatedAt: Date;
  tokensUsed: { generation: number; validation: number };
}> => {
  try {
    const token = await getAuthToken();
    const response = await axios.post(`${BACKEND_URL}/api/content/generate`, request, {
      headers: { Authorization: `Bearer ${token}` },
      timeout: 120000 // 2 minuten timeout voor AI generatie
    });
    return response.data;
  } catch (error: any) {
    console.error('Fout bij genereren artikel', error);
    throw new Error(error.response?.data?.message || error.message || 'Fout bij genereren artikel');
  }
};

/**
 * Valideer een artikel met OpenAI (admin)
 */
export const validateArticle = async (articleId: string, content: {
  title: string;
  metaDescription: string;
  h1: string;
  intro: string;
  body: string;
  faqSchema?: FAQItem[];
  citableFacts?: CitableFact[];
}): Promise<{ validation: ArticleValidation }> => {
  try {
    const token = await getAuthToken();
    const response = await axios.post(
      `${BACKEND_URL}/api/content/validate`,
      { articleId, content },
      {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 60000
      }
    );
    return response.data;
  } catch (error: any) {
    console.error('Fout bij valideren artikel', error);
    throw new Error(error.response?.data?.message || error.message || 'Fout bij valideren artikel');
  }
};

// ============================================
// ADMIN STATS & QUEUE
// ============================================

/**
 * Haal admin statistieken op
 */
export const getContentAdminStats = async (): Promise<ContentAdminStats> => {
  try {
    const token = await getAuthToken();
    const response = await axios.get(`${BACKEND_URL}/api/content/admin/stats`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  } catch (error: any) {
    console.error('Fout bij ophalen statistieken', error);
    throw new Error(error.response?.data?.message || error.message || 'Fout bij ophalen statistieken');
  }
};

/**
 * Haal review queue op
 */
export const getReviewQueue = async (): Promise<ReviewQueueItem[]> => {
  try {
    const token = await getAuthToken();
    const response = await axios.get(`${BACKEND_URL}/api/content/admin/queue`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  } catch (error: any) {
    console.error('Fout bij ophalen review queue', error);
    throw new Error(error.response?.data?.message || error.message || 'Fout bij ophalen review queue');
  }
};

// ============================================
// HELPER UTILITIES
// ============================================

/**
 * Get status badge color
 */
export const getStatusColor = (status: ArticleStatus): string => {
  const colors: Record<ArticleStatus, string> = {
    draft: 'bg-gray-100 text-gray-700',
    review: 'bg-yellow-100 text-yellow-700',
    published: 'bg-green-100 text-green-700',
    archived: 'bg-red-100 text-red-700',
  };
  return colors[status] || colors.draft;
};

/**
 * Get score color
 */
export const getScoreColor = (score: number): string => {
  if (score >= 85) return 'text-green-600';
  if (score >= 70) return 'text-yellow-600';
  if (score >= 50) return 'text-orange-500';
  return 'text-red-600';
};

/**
 * Get score background color
 */
export const getScoreBgColor = (score: number): string => {
  if (score >= 85) return 'bg-green-100';
  if (score >= 70) return 'bg-yellow-100';
  if (score >= 50) return 'bg-orange-100';
  return 'bg-red-100';
};

/**
 * Format date for display
 */
export const formatDate = (date: Date | string | null): string => {
  if (!date) return '-';
  const d = new Date(date);
  return d.toLocaleDateString('nl-NL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Format relative time
 */
export const formatRelativeTime = (date: Date | string | null): string => {
  if (!date) return '-';
  const d = new Date(date);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  
  if (minutes < 60) return `${minutes} min geleden`;
  if (hours < 24) return `${hours} uur geleden`;
  if (days < 7) return `${days} dagen geleden`;
  return formatDate(date);
};

/**
 * Truncate text
 */
export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - 3) + '...';
};